<template>
  <div class="settings-panel">
    <header class="settings-panel-header">
      <button
        class="settings-panel-close"
        aria-label="Close"
        @click="$emit('close')"
      >
        ×
      </button>
      <h2 class="settings-panel-title">
        Dashboard settings
      </h2>
      <button
        class="settings-save-btn"
        :disabled="saving || !dirty"
        @click="onSave"
      >
        {{ saving ? 'Saving…' : 'Save' }}
      </button>
    </header>

    <div class="settings-panel-body">
      <div
        v-if="loading"
        class="settings-panel-loading"
      >
        Loading…
      </div>

      <template v-else>
        <div class="settings-row">
          <div class="settings-row-text">
            <span class="settings-row-label">Allow managers to view revenue widgets</span>
            <span class="settings-row-help">
              When off, invoices and other revenue &amp; financial widgets are visible to owners only. Managers will no longer see them. Note - Admins with permissions lower than managers never see revenue widgets.
            </span>
          </div>
          <label
            class="settings-switch"
            :class="{ 'settings-switch--on': managersCanViewRevenue }"
          >
            <input
              v-model="managersCanViewRevenue"
              type="checkbox"
              class="settings-switch-input"
              :disabled="saving"
            >
            <span class="settings-switch-track" />
            <span class="settings-switch-thumb" />
          </label>
        </div>

        <p
          v-if="saveError"
          class="settings-save-error"
        >
          {{ saveError }}
        </p>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useDashboardStorage } from '../composables/useDashboardStorage.js'

const emit = defineEmits(['close'])

const { loadDashboard, saveOrgSettings } = useDashboardStorage()

const loading = ref(true)
const saving = ref(false)
const saveError = ref('')
const managersCanViewRevenue = ref(true)
// Last persisted value, used to detect unsaved changes
const savedValue = ref(true)

const dirty = computed(() => managersCanViewRevenue.value !== savedValue.value)

onMounted(async () => {
  const result = await loadDashboard()
  const value = result?.managers_can_view_revenue !== false
  managersCanViewRevenue.value = value
  savedValue.value = value
  loading.value = false
})

const onSave = async () => {
  saving.value = true
  saveError.value = ''
  const target = managersCanViewRevenue.value
  const ok = await saveOrgSettings(target)
  saving.value = false
  if (ok) {
    savedValue.value = target
    emit('close')
  } else {
    saveError.value = 'Could not save. Only organization owners can change this setting.'
  }
}
</script>

<style scoped>
.settings-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
}

.settings-panel-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 20px 24px;
  border-bottom: 1px solid #eef0f2;
}

.settings-panel-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
}

.settings-panel-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  font-size: 24px;
  line-height: 1;
  color: rgba(0, 0, 0, 0.5);
  cursor: pointer;
  border-radius: 4px;
}

.settings-panel-close:hover {
  background: #f3f4f6;
  color: rgba(0, 0, 0, 0.87);
}

.settings-save-btn {
  margin-left: auto;
  height: 36px;
  min-width: 72px;
  padding: 0 16px;
  background-color: #2383E2;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;
}

.settings-save-btn:hover:not(:disabled) {
  background-color: #1b6fc4;
}

.settings-save-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.settings-panel-body {
  padding: 24px;
}

.settings-panel-loading {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.5);
}

.settings-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.settings-row-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.settings-row-label {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
}

.settings-row-help {
  font-size: 13px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.55);
}

.settings-switch {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  flex-shrink: 0;
  cursor: pointer;
  margin-top: 2px;
}

.settings-switch-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
}

.settings-switch-track {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.38);
  border-radius: 10px;
  transition: background 0.2s, opacity 0.2s;
}

.settings-switch-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s;
}

.settings-switch--on .settings-switch-track {
  background: #2383E2;
  opacity: 1;
}

.settings-switch--on .settings-switch-thumb {
  transform: translateX(16px);
}

.settings-save-error {
  margin: 16px 0 0;
  font-size: 13px;
  color: #d33;
}
</style>
